import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchServices, fetchProducts } from '../services/api';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/auth');
      return;
    }
    try {
      const parsed = JSON.parse(userData);
      if (parsed.user_type !== 'vendor') {
        navigate('/');
        return;
      }
      setUser(parsed);
      loadListings(parsed.id);
    } catch (err) {
      navigate('/auth');
    }
  }, [navigate]);

  // Listen for location changes to refresh data when coming back from edit
  useEffect(() => {
    if (user) {
      loadListings(user.id);
    }
  }, [location]);

  const loadListings = async (userId) => {
    try {
      setLoading(true);
      const [servicesData, productsData] = await Promise.all([
        fetchServices({ user_id: userId }),
        fetchProducts({ user_id: userId }),
      ]);
      const servicesList = servicesData.data || servicesData;
      const productsList = productsData.data || productsData;
      setServices(Array.isArray(servicesList) ? servicesList : []);
      setProducts(Array.isArray(productsList) ? productsList : []);
    } catch (err) {
      setError('Failed to load listings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div style={{ backgroundColor: 'var(--background)', minHeight: '100vh' }}>
      {/* Dynamic Styles for Hover Effects */}
      <style>{`
        .stat-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .stat-card:hover { transform: translateY(-4px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
        .listing-item { transition: background-color 0.2s ease; }
        .listing-item:hover { background-color: var(--surface-2) !important; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade { animation: fadeIn 0.4s ease forwards; }
      `}</style>

      {/* Modern Header Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderBottom: '1px solid var(--border)', 
        padding: '40px 20px',
        marginBottom: '20px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px' }}>
          <div className="animate-fade">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold' }}>
                    {user.name.charAt(0)}
                </div>
                <div>
                    <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 800, color: 'var(--text-1)', margin: 0 }}>
                        Dashboard
                    </h1>
                    <p style={{ color: 'var(--text-3)', margin: 0, fontSize: '0.95rem' }}>Welcome back, <span style={{color: 'var(--text-1)', fontWeight: 600}}>{user.name}</span></p>
                </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }} className="animate-fade">
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/vendor-edit-profile')}
              style={{ padding: '10px 20px', borderRadius: '10px', fontWeight: 600 }}
            >
              Edit Profile
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/vendor/${user.id}`)}
              style={{ padding: '10px 20px', borderRadius: '10px', fontWeight: 600, boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)' }}
            >
              Public View
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 20px 40px', maxWidth: '1200px', margin: '0 auto' }}>
        {error && (
          <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '12px', padding: '16px', marginBottom: '24px', color: '#991B1B', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{fontSize: '20px'}}>⚠️</span> {error}
          </div>
        )}

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '40px' }}>
          {[
            { label: 'Active Services', value: services.length, color: 'var(--primary)', icon: '🛠️' },
            { label: 'Active Products', value: products.length, color: 'var(--accent)', icon: '📦' },
            { label: 'Average Rating', value: user.rating ? (typeof user.rating === 'number' ? user.rating.toFixed(1) : parseFloat(user.rating).toFixed(1)) : '0.0', color: '#EAB308', icon: '⭐' }
          ].map((stat, idx) => (
            <div key={idx} className="stat-card animate-fade" style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '20px', padding: '24px', position: 'relative', overflow: 'hidden', animationDelay: `${idx * 0.1}s` }}>
                <div style={{ fontSize: '24px', marginBottom: '12px' }}>{stat.icon}</div>
                <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-1)', marginBottom: '4px' }}>{stat.value}</div>
                <div style={{ color: 'var(--text-3)', fontWeight: 500, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</div>
                <div style={{ position: 'absolute', right: '-10px', bottom: '-10px', fontSize: '60px', opacity: 0.03 }}>{stat.icon}</div>
            </div>
          ))}
        </div>

        {/* Two Column Layout for Listings */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '32px' }}>
          
          {/* Services Section */}
          <section className="animate-fade" style={{animationDelay: '0.3s'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
                Services
                <span style={{ fontSize: '0.75rem', padding: '4px 10px', background: 'var(--surface-2)', borderRadius: '20px', color: 'var(--text-3)' }}>{services.length}</span>
              </h2>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/add-listing')} style={{ borderRadius: '8px' }}>+ New Service</button>
            </div>
            
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}><div className="loader"></div></div>
            ) : services.length === 0 ? (
              <EmptyState title="No services yet" subtitle="Start reaching customers by listing your first service." onAdd={() => navigate('/add-listing')} />
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {services.map(service => (
                  <ListingCard 
                    key={service.id} 
                    title={service.name} 
                    subtitle={service.category?.name || 'Professional Service'}
                    onEdit={() => navigate(`/edit-service/${service.id}`)}
                    onView={() => navigate(`/service/${service.id}`)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Products Section */}
          <section className="animate-fade" style={{animationDelay: '0.4s'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
                Products
                <span style={{ fontSize: '0.75rem', padding: '4px 10px', background: 'var(--surface-2)', borderRadius: '20px', color: 'var(--text-3)' }}>{products.length}</span>
              </h2>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/add-listing')} style={{ borderRadius: '8px' }}>+ New Product</button>
            </div>
            
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}><div className="loader"></div></div>
            ) : products.length === 0 ? (
              <EmptyState title="No products yet" subtitle="Showcase your goods to local buyers today." onAdd={() => navigate('/add-listing')} />
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {products.map(product => (
                  <ListingCard 
                    key={product.id} 
                    title={product.name} 
                    subtitle={`₹${product.price}`}
                    onView={() => navigate(`/product/${product.id}`)}
                    isProduct
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

/* Helper Component for Listing Cards */
const ListingCard = ({ title, subtitle, onEdit, onView, isProduct }) => (
    <div className="listing-item" style={{ 
      background: 'white', 
      border: '1px solid var(--border)', 
      borderRadius: '16px', 
      padding: '16px', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flex: 1 }} onClick={onView}>
        <div style={{ 
            width: '44px', height: '44px', borderRadius: '10px', 
            background: isProduct ? '#fdf2f8' : '#f5f3ff', 
            display: 'flex', alignItems: 'center', justifyContent: 'center' 
        }}>
            {isProduct ? '📦' : '🛠️'}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-1)' }}>{title}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-3)', marginTop: '2px' }}>{subtitle}</div>
        </div>
      </div>
      <button className="btn btn-secondary btn-sm" onClick={onEdit || onView} style={{ borderRadius: '8px', padding: '6px 16px', marginLeft: '8px', flexShrink: 0 }}>
        {onEdit ? 'Edit' : 'View'}
      </button>
    </div>
);

/* Helper Component for Empty States */
const EmptyState = ({ title, subtitle, onAdd }) => (
    <div style={{ background: 'white', border: '2px dashed var(--border)', borderRadius: '20px', padding: '40px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: '40px', marginBottom: '16px' }}>📭</div>
      <h3 style={{ fontWeight: 700, marginBottom: '8px', color: 'var(--text-1)' }}>{title}</h3>
      <p style={{ color: 'var(--text-3)', fontSize: '0.9rem', marginBottom: '24px', lineHeight: 1.5 }}>{subtitle}</p>
      <button className="btn btn-primary btn-sm" onClick={onAdd}>Create Listing</button>
    </div>
);

export default VendorDashboard;